<script setup lang="ts">
import MapAreaLayer from "@/areas/MapAreaLayer.vue";
import { coordinatesToMapBounds } from "@/areas/mapUtils";
import type { Canvass, CanvassHouse } from "@/canvasses/types";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import { MapContainerAsync as MapContainer } from "@/components/map/MapContainerAsync";
import { MapGeolocationAsync as MapGeolocation } from "@/components/map/MapGeolocationAsync";
import { MapMarkerAsync as MapMarker } from "@/components/map/MapMarkerAsync";
import MapMarkerIcon from "@/components/map/MapMarkerIcon.vue";
import { QuestionMark } from "@iconoir/vue";
import { computed, onDeactivated, ref } from "vue";
import { RouterView, useRoute, useRouter } from "vue-router";
import CanvassAreaMapLegendDialog from "./CanvassAreaMapLegendDialog.vue";
import CanvassAreaMapMeetingPointDialog from "./CanvassAreaMapMeetingPointDialog.vue";
import MapMarkerCanvassHouseIcon from "./MapMarkerCanvassHouseIcon.vue";
import TurfSelect from "./TurfSelect.vue";
import { useTurfs } from "./useTurfs";

const { canvass } = defineProps<{
  canvass: Canvass;
}>();

// name is used to selectively keep this component alive,
// via <KeepAlive include="name">
defineOptions({ name: "CanvassAreaPage" });

const route = useRoute();
const router = useRouter();

const meetingPointIsSelected = ref(false);
const legendIsOpen = ref(false);

function closeAll() {
  meetingPointIsSelected.value = false;
  legendIsOpen.value = false;
}

function closeHouseSheet() {
  if (route.name !== "canvassFlowArea") {
    void router.push({ name: "canvassFlowArea" });
  }
}

function selectHouse(house: CanvassHouse) {
  closeAll();
  void router.push({
    name: "canvassFlowHouseDetails",
    params: { houseId: house.id },
  });
}

function selectMeetingPoint() {
  closeAll();
  closeHouseSheet();
  meetingPointIsSelected.value = true;
}

function toggleLegend() {
  const legendDialogStatus = legendIsOpen.value;
  closeAll();
  closeHouseSheet();
  legendIsOpen.value = !legendDialogStatus;
}

const {
  turfIds,
  selectedTurfId,
  filteredCanvassHouses,
  getCanvassHousesStatus,
} = useTurfs(() => canvass);

const positionOptions = computed(() => ({
  bounds: coordinatesToMapBounds(canvass.area.coordinates),
}));

onDeactivated(() => {
  closeAll();
});
</script>

<template>
  <HandleQueryStatus :query-status="getCanvassHousesStatus">
    <div v-if="canvass !== undefined" class="canvass-area-view">
      <div class="map">
        <MapContainer v-slot="{ mapInstance }" :options="positionOptions">
          <MapAreaLayer :area="canvass.area" :map-instance="mapInstance" />
          <MapMarker
            v-for="house in filteredCanvassHouses"
            :key="house.id"
            :lnglat="house.coordinates"
            :map-instance="mapInstance"
            @click="selectHouse(house)"
          >
            <MapMarkerCanvassHouseIcon :canvass-house="house" class="map-pin" />
          </MapMarker>
          <MapMarker
            v-if="canvass.meetingPoint.coordinates"
            :lnglat="canvass.meetingPoint.coordinates"
            :map-instance="mapInstance"
            @click="selectMeetingPoint"
          >
            <MapMarkerIcon class="map-marker-meetingpoint map-pin" />
          </MapMarker>
          <MapGeolocation :map-instance="mapInstance" />
        </MapContainer>

        <div class="controls">
          <button
            class="button legend-button button--secondary"
            aria-label="Karten Legende"
            @click="toggleLegend"
          >
            <QuestionMark />
          </button>
          <TurfSelect
            v-if="turfIds.length > 1"
            v-model="selectedTurfId"
            :turf-ids="turfIds"
          />
        </div>

        <CanvassAreaMapMeetingPointDialog
          v-if="meetingPointIsSelected"
          :canvass="canvass"
          @close="closeAll"
        />
        <CanvassAreaMapLegendDialog v-if="legendIsOpen" @close="closeAll" />
        <RouterView v-slot="{ Component }">
          <component
            :is="Component"
            :key="route.params.houseId"
            :canvass="canvass"
          />
        </RouterView>
      </div>
    </div>
  </HandleQueryStatus>
</template>

<style scoped>
.canvass-area-view {
  block-size: calc(100dvh - var(--canvass-flow-bottom-nav-height));
  display: flex;
  flex-direction: column;
}

.map {
  position: relative;
  flex-grow: 1;
}

.map-pin {
  cursor: pointer;
}

.controls {
  position: absolute;
  left: var(--spacing-sm);
  top: var(--spacing-sm);
  display: flex;
  gap: var(--spacing-sm);
}

.legend-button {
  font-size: var(--font-size-lg);
  padding: var(--padding-input);
  border-radius: var(--border-radius-input);
  border: none;
  background-color: white;
  box-shadow: var(--shadow);
  cursor: pointer;
}
</style>
